import React, { useRef, useState, useEffect } from 'react';
import { UpdatePromoterPayload } from '../promoter.service';

interface EditPromoterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: UpdatePromoterPayload) => void;
  currentData: {
    name: string;
    email?: string;
    commissionRate: number;
  };
}

export const EditPromoterModal: React.FC<EditPromoterModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentData
}) => {
  const [formData, setFormData] = useState({
    name: currentData.name,
    email: currentData.email || '',
    commissionRate: currentData.commissionRate
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const commissionRateSliderRef = useRef<HTMLInputElement>(null);

  // Update form data when currentData changes
  useEffect(() => {
    setFormData({
      name: currentData.name,
      email: currentData.email || '',
      commissionRate: currentData.commissionRate
    });
  }, [currentData]);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (formData.commissionRate < 0 || formData.commissionRate > 100) {
      newErrors.commissionRate = 'Commission rate must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onConfirm({
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        commissionRate: formData.commissionRate
      });
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: currentData.name,
      email: currentData.email || '',
      commissionRate: currentData.commissionRate
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Edit Promoter</h2>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Promoter name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              ref={emailInputRef}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Promoter email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Commission Rate Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Commission Rate (%) *
            </label>
            
            {/* Slider */}
            <div className="mb-4">
              <input
                ref={commissionRateSliderRef}
                type="range"
                min="0"
                max="100"
                step="1"
                value={formData.commissionRate}
                onChange={(e) => handleInputChange('commissionRate', parseInt(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer range-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
            
            {/* Commission Rate Display */}
            <div className="text-center">
              <div className="inline-flex items-center p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl mr-4 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-700">
                    {formData.commissionRate}%
                  </div>
                  <div className="text-sm text-emerald-600 font-medium">Commission Rate</div>
                </div>
              </div>
            </div>
            
            {errors.commissionRate && (
              <p className="mt-2 text-sm text-red-600 text-center">{errors.commissionRate}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all transform hover:scale-105"
          >
            Update Promoter
          </button>
        </div>
      </div>
    </div>
  );
};
